import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Layers2, Loader2 } from 'lucide-react';
import { usePlanEditor } from './PlanEditorContext';
import { FoldableSection } from './FoldableSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const GHOST_COLORS = ['#38BDF8', '#F472B6', '#34D399', '#FBBF24', '#A78BFA', '#FB7185'];

/**
 * GhostFloorsControl — V14.72 « Superposition étages » (onion skin).
 * Coche un ou plusieurs autres niveaux : leurs murs + contour extérieur
 * s'affichent en translucide sur le canvas pour vérifier l'alignement
 * vertical (murs extérieurs, trémies, refends).
 */
export const GhostFloorsControl = ({ projectId, selectedLotId, disabled }) => {
  const { ghostStructures, setGhostStructures } = usePlanEditor();
  const [available, setAvailable] = useState(null); // null = pas encore chargé
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/projects/${projectId}/plan-source/structures-overview`,
        { withCredentials: true });
      setAvailable((r.data?.lots || []).filter((l) => l.lot_id !== selectedLotId));
    } catch {
      setAvailable([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, selectedLotId]);

  useEffect(() => { load(); }, [load]);
  // Reset des fantômes quand on change de lot
  useEffect(() => () => setGhostStructures([]), [selectedLotId, setGhostStructures]);

  const toggle = (lot, idx) => {
    setGhostStructures((prev) => {
      const exists = prev.some((g) => g.lot_id === lot.lot_id);
      if (exists) return prev.filter((g) => g.lot_id !== lot.lot_id);
      return [...prev, {
        lot_id: lot.lot_id,
        label: lot.label,
        color: GHOST_COLORS[idx % GHOST_COLORS.length],
        walls: lot.walls,
        outer_polygon: lot.outer_polygon,
      }];
    });
  };

  if (available !== null && available.length === 0) return null;

  return (
    <FoldableSection
      title="Superposition étages"
      icon={Layers2}
      iconClass="text-sky-300"
      defaultOpen={false}
      testid="ghost-floors-section"
      badge={loading
        ? <Loader2 className="w-3 h-3 animate-spin text-gray-500" />
        : (ghostStructures.length > 0
          ? <span className="text-[9px] px-1 rounded bg-sky-500/20 text-sky-300">{ghostStructures.length} actif(s)</span>
          : null)}
    >
      <p className="text-[9.5px] text-gray-500 mt-0.5 leading-snug">
        Affiche les murs des autres niveaux en translucide pour vérifier
        l'alignement des murs extérieurs.
      </p>
      <div className="mt-1.5 space-y-1">
        {(available || []).map((lot, idx) => {
          const active = ghostStructures.some((g) => g.lot_id === lot.lot_id);
          const color = GHOST_COLORS[idx % GHOST_COLORS.length];
          return (
            <button
              key={lot.lot_id}
              type="button"
              onClick={() => toggle(lot, idx)}
              disabled={disabled}
              className={`w-full flex items-center gap-2 px-2 py-1 rounded border text-[11px] transition-colors ${
                active ? 'bg-white/[0.06]' : 'border-white/10 hover:bg-white/[0.04]'
              }`}
              style={active ? { borderColor: color } : undefined}
              data-testid={`ghost-toggle-${lot.lot_id}`}
            >
              <span className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ background: color, opacity: active ? 1 : 0.35 }} />
              <span className={active ? 'text-white' : 'text-gray-400'}>
                {lot.label}
              </span>
              <span className="ml-auto text-[9.5px] text-gray-500">
                {lot.walls_count} mur{lot.walls_count > 1 ? 's' : ''}
              </span>
            </button>
          );
        })}
      </div>
      {ghostStructures.length > 0 && (
        <button
          type="button"
          onClick={() => setGhostStructures([])}
          className="mt-1.5 text-[10px] text-gray-500 underline underline-offset-2 hover:text-gray-300"
          data-testid="ghost-clear-btn"
        >
          Masquer tous les calques
        </button>
      )}
    </FoldableSection>
  );
};
